import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import "../css/others/Card.css";
import "../css/others/Table.css";
import BackButton from "../Reusablecomponents/BackButton";
import { apiCall } from "../main/ApiCall";
import ToastPopup from "../popups/ToastPopup";
import { FunctionDetailsInfo, EnumOption, createToastMessage, ToastMessage, Role, User} from "../interFaces/interfaces";
import { validateForm } from "../Reusablecomponents/Validation";
const CreateFunction: React.FC = () => {
  const [functionData, setFunctionData] = useState<FunctionDetailsInfo>({ functionName: "", functionHeadName: "", functionHeadEmail: "", functionSpocName: "", functionSpocEmail: "" });
  const [functionOptions, setFunctionOptions] = useState<EnumOption[]>([]);
  const [, setRoles] = useState<Role[]>([]);
  const [functionHeadUsers, setFunctionHeadUsers] = useState<User[]>([]);
  const [functionSpocUsers, setFunctionSpocUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [loadingHeadUsers, setLoadingHeadUsers] = useState<boolean>(false);
  const [loadingSpocUsers, setLoadingSpocUsers] = useState<boolean>(false);
  const [message, setMessage] = useState<ToastMessage | null>(null);
  const [, setFunctionHeadRoleId] = useState<string>("");
  const [, setFunctionSpocRoleId] = useState<string>("");
  const [, setHeadDropdownClicked] = useState<boolean>(false);
  const [, setSpocDropdownClicked] = useState<boolean>(false);
  const [hasHeadUsers, setHasHeadUsers] = useState<boolean>(true);
  const [hasSpocUsers, setHasSpocUsers] = useState<boolean>(true);
  const createdBy = localStorage.getItem("email");
  const clientId = localStorage.getItem("clientId");
  const navigate = useNavigate();
  const fetchUsersByRole = useCallback(
    async (roleId: string, userType: "head" | "spoc") => {
      if (!clientId || !roleId) return;
      if (userType === "head") {
        setLoadingHeadUsers(true);
      } else {
        setLoadingSpocUsers(true);
      }
      try {
        const response = await apiCall("get", `/api/users/role?clientId=${clientId}&roleId=${roleId}&page=0&size=100`);
        const users = response?.content || [];
        if (userType === "head") {
          setFunctionHeadUsers(users);
          setHasHeadUsers(users.length > 0);
          if (users.length === 0) {
            setMessage(createToastMessage("No Function Head users found. Please add users in User Management, then create the function.", "warning", 3000))
          }
        } else {
          setFunctionSpocUsers(users);
          setHasSpocUsers(users.length > 0);
          if (users.length === 0) {
            setMessage(createToastMessage("No Function SPOC users found. Please add users in User Management, then create the function.", "warning", 3000));
          }
        }
      } catch (error) {
        setMessage(createToastMessage(`Failed to load ${userType === "head" ? "Function Head" : "Function SPOC"} users. Please add users in User Management, then create the function.`, "warning", 3000));
        if (userType === "head") {
          setHasHeadUsers(false);
        } else {
          setHasSpocUsers(false);
        }
      } finally {
        if (userType === "head") {
          setLoadingHeadUsers(false);
        } else {
          setLoadingSpocUsers(false);
        }
      }
    },
    [clientId]
  );
  useEffect(() => {
    const fetchInitialData = async () => {
      setLoading(true);
      try {
        const functionResponse = await apiCall("get", "/api/enums/FunctionEnum");
        setFunctionOptions(functionResponse);
        if (clientId) {
          const rolesResponse = await apiCall("get", `/api/roles/all/${clientId}`);
          setRoles(rolesResponse);
          const functionHeadRole = rolesResponse.find((role: Role) => role.name === "Function Head");
          const functionSpocRole = rolesResponse.find((role: Role) => role.name === "Function Spoc");
          const headRoleId = functionHeadRole?.id || "";
          const spocRoleId = functionSpocRole?.id || "";
          if (headRoleId) {
            setFunctionHeadRoleId(headRoleId);
          }
          if (spocRoleId) {
            setFunctionSpocRoleId(spocRoleId);
          }
          if (headRoleId) {
            await fetchUsersByRole(headRoleId, "head");
          } else {
            setHasHeadUsers(false);
            setMessage(createToastMessage("Function Head role not found. Please configure roles in User Management.", "warning", 3000));
          }
          if (spocRoleId) {
            await fetchUsersByRole(spocRoleId, "spoc");
          } else {
            setHasSpocUsers(false);
            setMessage(createToastMessage("Function SPOC role not found. Please configure roles in User Management.", "warning", 3000));
          }
        }
      } catch (error) {
        setMessage(createToastMessage("Failed to load initial data", "error", 3000));
      } finally {
        setLoading(false);
      }
    };
    fetchInitialData();
  }, [clientId, fetchUsersByRole, setRoles, setFunctionHeadRoleId, setFunctionSpocRoleId]);
  const handleFunctionNameChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const { value } = event.target;
    setFunctionData({ ...functionData, functionName: value });
  };
  const handleFunctionHeadChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedUserId = event.target.value;
    if (selectedUserId) {
      const selectedUser = functionHeadUsers.find((user) => user.id === selectedUserId);
      if (selectedUser) {
        setFunctionData({ ...functionData, functionHeadName: `${selectedUser.firstName} ${selectedUser.lastName}`.trim(), functionHeadEmail: selectedUser.email });
      }
    } else {
      setFunctionData({ ...functionData, functionHeadName: "", functionHeadEmail: "" });
    }
  };
  const handleFunctionSpocChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedUserId = event.target.value;
    if (selectedUserId) {
      const selectedUser = functionSpocUsers.find((user) => user.id === selectedUserId);
      if (selectedUser) {
        setFunctionData({ ...functionData, functionSpocName: `${selectedUser.firstName} ${selectedUser.lastName}`.trim(), functionSpocEmail: selectedUser.email });
      }
    } else {
      setFunctionData({ ...functionData, functionSpocName: "", functionSpocEmail: "" });
    }
  };
  const handleFunctionHeadDropdownClick = () => { setHeadDropdownClicked(true) };
  const handleFunctionSpocDropdownClick = () => { setSpocDropdownClicked(true) };
  const handleSubmit = async () => {
    if (!hasHeadUsers || !hasSpocUsers) {
      setMessage(createToastMessage("Please add Function Head and Function SPOC users in User Management, then create the function.", "warning", 3000));
      return;
    }
    const { isValid, errors } = validateForm({ functionName: { value: functionData.functionName, rules: [{ required: true }], name: "Function" }, functionHeadName: { value: functionData.functionHeadName, rules: [{ required: true }], name: "Function Head Name" }, functionHeadEmail: { value: functionData.functionHeadEmail, rules: [{ required: true }], name: "Function Head Email" }, functionSpocName: { value: functionData.functionSpocName, rules: [{ required: true }], name: "Function SPOC Name" }, functionSpocEmail: { value: functionData.functionSpocEmail, rules: [{ required: true }], name: "Function SPOC Email" } });
    if (!isValid) {
      const firstError = Object.values(errors)[0];
      setMessage(createToastMessage(firstError, "warning", 3000));
      return;
    }
    try {
      const formattedFunction = { ...functionData, createdBy, clientId };
      await apiCall("post", "/api/functions", formattedFunction);
      setMessage(createToastMessage("Function created successfully!", "success", 2500));
      setTimeout(() => {
        navigate("/functions");
      }, 3000);
    } catch (error: any) {
      const errorMessage = error?.response?.data?.message || "Failed to create function";
      setMessage(createToastMessage(errorMessage, "error", 3000));
    }
  };
  return (
    <div className="container">
      <h1 className="title">Create Function</h1>
      <div className="grid">
        <div className="card">
          <div className="card-inner">
            <div className="content">
              <div className="label">Function Name</div>
              {loading ? (
                <select disabled className="loading-select">
                  <option>Loading options...</option>
                </select>
              ) : (
                <select value={functionData.functionName} onChange={handleFunctionNameChange} className={functionData.functionName ? "selected" : ""}>
                  <option value="">Select a Function Name</option>
                  {functionOptions.map((option) => (
                    <option key={option.value} value={option.value}>{option.description}</option>
                  ))}
                </select>
              )}
            </div>
          </div>
        </div>
        <div className="card">
          <div className="card-inner">
            <div className="content">
              <div className="label">Function Head</div>
              {loadingHeadUsers ? (
                <select disabled className="loading-select"><option>Loading users...</option></select>
              ) : (
                <select value={functionHeadUsers.find((user) => `${user.firstName} ${user.lastName}`.trim() === functionData.functionHeadName)?.id || ""} onChange={handleFunctionHeadChange} onClick={handleFunctionHeadDropdownClick}>
                  <option value="">Select Function Head</option>
                  {functionHeadUsers.length > 0 ? (
                    functionHeadUsers.map((user) => (
                      <option key={user.id} value={user.id}>{user.firstName} {user.lastName} ({user.email})</option>
                    ))
                  ) : (
                    <option value="" disabled>No Function Head users found</option>
                  )}
                </select>
              )}
            </div>
          </div>
        </div>
        <div className="card">
          <div className="card-inner">
            <div className="content">
              <div className="label">Function Head Email</div>
              <input type="email" value={functionData.functionHeadEmail} readOnly disabled placeholder="Auto-filled from selection" />
            </div>
          </div>
        </div>
        <div className="card">
          <div className="card-inner">
            <div className="content">
              <div className="label">Function SPOC</div>
              {loadingSpocUsers ? (
                <select disabled className="loading-select">
                  <option>Loading users...</option>
                </select>
              ) : (
                <select value={functionSpocUsers.find((user) => `${user.firstName} ${user.lastName}`.trim() === functionData.functionSpocName)?.id || ""} onChange={handleFunctionSpocChange} onClick={handleFunctionSpocDropdownClick}>
                  <option value="">Select Function SPOC</option>
                  {functionSpocUsers.length > 0 ? (
                    functionSpocUsers.map((user) => (
                      <option key={user.id} value={user.id}>{user.firstName} {user.lastName} ({user.email})</option>
                    ))
                  ) : (
                    <option value="" disabled> No Function SPOC users found</option>
                  )}
                </select>
              )}
            </div>
          </div>
        </div>
        <div className="card">
          <div className="card-inner">
            <div className="content">
              <div className="label">Function SPOC Email</div>
              <input type="email" value={functionData.functionSpocEmail} readOnly disabled placeholder="Auto-filled from selection" />
            </div>
          </div>
        </div>
      </div>
      <div className="buttons">
        <BackButton className="back-button2" label="Back" />
        <button className="submit-button2" onClick={handleSubmit}>Submit</button>
      </div>
      {message && (<ToastPopup message={message.text} type={message.type} duration={message.duration} onClose={() => setMessage(null)} />)}
    </div>
  );
};
export default CreateFunction;